import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Trash2, EyeOff, Eye, Pencil, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Loader, Empty } from "@/components/Bits";

const KINDS = ["petitions", "blogs", "discussions", "events", "spotlights"];
// petitions use the quick title/summary/body modal below; blogs and events
// reuse the full CreateBlog/CreateEvent forms in edit mode instead.
const MODAL_EDITABLE = ["petitions"];
const FORM_EDITABLE = ["blogs", "events"];
const field = "w-full bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-neon/60 transition-colors";

const idOf = (kind: string, item: any) =>
  item.petition_id || item.blog_id || item.discussion_id || item.event_id || item.spotlight_id;
const titleOf = (kind: string, item: any) => (kind === "spotlights" ? item.name : item.title);
const authorOf = (item: any) => item.creator_name || item.author_name || item.organizer_name || item.owner_name || "";

export const ManageContent = () => {
  const navigate = useNavigate();
  const [kind, setKind] = useState("petitions");
  const [items, setItems] = useState<any[] | null>(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<any>(null);
  const [ef, setEf] = useState({ title: "", summary: "", body: "" });

  const load = useCallback(() => {
    setItems(null);
    api.get(`/admin/content/${kind}`, { params: { q: q || undefined } })
      .then((r) => setItems(r.data))
      .catch((e) => { toast.error(errMsg(e)); setItems([]); });
  }, [kind, q]);
  useEffect(load, [load]);

  const publish = async (item: any, on: boolean) => {
    try {
      await api.post(`/admin/content/${kind}/${idOf(kind, item)}/publish`, null, { params: { publish: on } });
      toast.success(on ? "Published." : "Unpublished — hidden from the public site.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (item: any) => {
    if (!window.confirm(`Permanently delete "${titleOf(kind, item)}" and all its related data?`)) return;
    try {
      await api.delete(`/admin/content/${kind}/${idOf(kind, item)}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startEdit = (item: any) => {
    if (kind === "blogs") return navigate(`/blogs/${idOf(kind, item)}/edit`);
    if (kind === "events") return navigate(`/events/${idOf(kind, item)}/edit`);
    setEditing(item);
    setEf({ title: titleOf(kind, item) || "", summary: item.summary || "", body: item.body || "" });
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.put(`/admin/content/${kind}/${idOf(kind, editing)}`, ef);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="admin-manage-content">
      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map((k) => (
          <button key={k} onClick={() => { setKind(k); setQ(""); }} data-testid={`manage-kind-${k}`}
            className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors ${
              kind === k ? "border-neon text-neon bg-neon/10" : "border-white/12 text-white/50 hover:text-white"}`}>
            {k}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 border border-white/10 px-3 h-9 bg-white/[0.03] focus-within:border-neon/60 transition-colors">
          <Search size={14} className="text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" data-testid="manage-search"
            className="bg-transparent outline-none text-sm w-44 placeholder:text-white/30" />
        </div>
      </div>

      {items === null ? <Loader /> : items.length === 0 ? (
        <div className="mt-8"><Empty text={`No ${kind} found.`} testid="manage-empty" /></div>
      ) : (
        <div className="mt-8 grid gap-3">
          {items.map((item, i) => (
            <div key={idOf(kind, item)} className="border border-white/10 bg-ink-surface p-5 flex flex-wrap items-center gap-4" data-testid={`manage-row-${i}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="font-outfit font-semibold">{titleOf(kind, item)}</div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-white/35">
                  {authorOf(item)}{item.city ? ` · ${item.city}` : ""}{item.category || item.topic ? ` · ${item.category || item.topic}` : ""}
                </div>
              </div>
              {item.status && (
                <Pill tone={item.status === "approved" ? "blue" : item.status === "pending" ? "yellow" : "neon"}>{item.status}</Pill>
              )}
              <div className="flex gap-2">
                {(MODAL_EDITABLE.includes(kind) || FORM_EDITABLE.includes(kind)) && (
                  <button onClick={() => startEdit(item)} data-testid={`manage-edit-${i}`} title="Edit"
                    className="w-9 h-9 grid place-items-center border border-white/15 hover:border-pcblue hover:text-pcblue transition-colors"><Pencil size={14} /></button>
                )}
                {kind !== "discussions" && (item.status === "approved" ? (
                  <button onClick={() => publish(item, false)} data-testid={`manage-unpublish-${i}`} title="Unpublish"
                    className="w-9 h-9 grid place-items-center border border-white/15 hover:border-pcyellow hover:text-pcyellow transition-colors"><EyeOff size={14} /></button>
                ) : (
                  <button onClick={() => publish(item, true)} data-testid={`manage-publish-${i}`} title="Publish"
                    className="w-9 h-9 grid place-items-center border border-white/15 hover:border-pcblue hover:text-pcblue transition-colors"><Eye size={14} /></button>
                ))}
                <button onClick={() => remove(item)} data-testid={`manage-delete-${i}`} title="Delete"
                  className="w-9 h-9 grid place-items-center border border-white/15 hover:border-neon hover:text-neon transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm grid place-items-center px-5 overflow-y-auto py-10" data-testid="manage-edit-modal">
          <form onSubmit={saveEdit} className="w-full max-w-2xl border border-white/15 bg-ink-surface p-8">
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-bebas text-3xl tracking-wide uppercase">Edit {kind.slice(0, -1)}</h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="manage-edit-close" className="text-white/40 hover:text-neon transition-colors"><X /></button>
            </div>
            <div className="mt-6 grid gap-4">
              <input value={ef.title} onChange={(e) => setEf({ ...ef, title: e.target.value })} data-testid="manage-edit-title" className={field} placeholder="Title" />
              <input value={ef.summary} onChange={(e) => setEf({ ...ef, summary: e.target.value })} data-testid="manage-edit-summary" className={field} placeholder="Summary" />
              <textarea rows={10} value={ef.body} onChange={(e) => setEf({ ...ef, body: e.target.value })} data-testid="manage-edit-body" className={field} placeholder="Body" />
              <button data-testid="manage-edit-save" className="h-11 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Save changes</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
